import { Cpu, Loader2, MapPin, Sparkles } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUILDING_TYPES } from "@/lib/apolloCalc";
import { useApolloCalculator } from "@/components/site/useApolloCalculator";
import { CalculatorIntro } from "@/components/site/CalculatorIntro";
import { LocationPicker } from "@/components/site/LocationPicker";
import { ResultCard } from "@/components/site/ResultCard";

const inputCls =
  "h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

const EmptyState = () => (
  <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-700 p-10 text-center">
    <Cpu className="h-10 w-10 text-emerald-400" />
    <p className="mt-4 font-display text-xl font-semibold text-white">Il risultato apparirà qui</p>
    <p className="mt-2 max-w-xs text-sm text-slate-400">Inserisci indirizzo e tipologia di stabile, poi premi "Calcola con Apollo.AI".</p>
  </div>
);

export const Calculator = () => {
  const { form, setForm, step, pin, setPin, loading, result, error, submit, confirm, back, mailStatus, mailError } = useApolloCalculator();

  return (
    <section id="calcolatore-ai" className="bg-[#0B0F17] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <CalculatorIntro />

        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          <form onSubmit={submit} data-testid="calculator-form" className="h-fit rounded-3xl bg-white p-6 sm:p-8 lg:col-span-5">
            <label className="text-sm font-medium text-slate-700" htmlFor="calc-address">Via e Città</label>
            <div className="relative mt-2">
              <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="calc-address"
                data-testid="calculator-address-input"
                className={`${inputCls} pl-10`}
                placeholder="Es. Via Roma 12, Milano · Via Nassa 5, Lugano"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <label className="mt-5 block text-sm font-medium text-slate-700">Tipologia stabile</label>
            <Select value={form.buildingType} onValueChange={(v) => setForm({ ...form, buildingType: v })}>
              <SelectTrigger data-testid="calculator-building-type-select" className="mt-2 h-12 rounded-xl text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUILDING_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`building-type-option-${t.value.toLowerCase().replace(/\s/g, "-")}`}>
                    {t.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="mt-5 block text-sm font-medium text-slate-700" htmlFor="calc-kwh">
              Consumo annuale stimato (kWh) <span className="font-normal text-slate-400">· opzionale</span>
            </label>
            <input
              id="calc-kwh"
              data-testid="calculator-consumption-input"
              type="number"
              min="0"
              className={`${inputCls} mt-2`}
              placeholder="Es. 3800"
              value={form.consumption}
              onChange={(e) => setForm({ ...form, consumption: e.target.value })}
            />
            <label className="mt-5 block text-sm font-medium text-slate-700" htmlFor="calc-coords">
              Coordinate GPS <span className="font-normal text-slate-400">· opzionale, più preciso</span>
            </label>
            <input
              id="calc-coords"
              data-testid="calculator-coords-input"
              className={`${inputCls} mt-2`}
              placeholder="Es. 46.0031, 8.9503"
              value={form.coords}
              onChange={(e) => setForm({ ...form, coords: e.target.value })}
            />
            {error && <p data-testid="calculator-error" className="mt-3 text-sm text-rose-600">{error}</p>}
            <button
              data-testid="calculator-submit-button"
              type="submit"
              disabled={loading}
              className="mt-7 inline-flex h-13 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-4 font-semibold text-slate-900 transition-colors hover:bg-emerald-400 disabled:opacity-70"
            >
              {loading && step === "form" ? <><Loader2 className="h-5 w-5 animate-spin" /> Ricerca dello stabile…</> : <><Sparkles className="h-5 w-5" /> Calcola con Apollo.AI</>}
            </button>
            <p className="mt-4 text-xs leading-relaxed text-slate-400">Prima confermi lo stabile sulla mappa satellitare, poi l'analisi con Google Solar API (dove disponibile, altrimenti stima per zona). Il dimensionamento definitivo avviene dopo il sopralluogo.</p>
          </form>
          <div className="lg:col-span-7">
            {step === "locate" && pin ? <LocationPicker pin={pin} setPin={setPin} onConfirm={confirm} onBack={back} loading={loading} />
              : result ? <ResultCard result={result} mailStatus={mailStatus} mailError={mailError} /> : <EmptyState />}
          </div>
        </div>
      </div>
    </section>
  );
};
